import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { diffValueEdits, applyValueEdits } from '../../../utils/mappingValueEdits';
import { saveProfileMappingValues as persistProfileMappingValues } from '../../../services/profileMappingValues';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCard from './FileCard';
import FileReviewDialog from './FileReviewDialog';
import BulkDeleteConfirmDialog, { collectBulkDeleteTargets } from './BulkDeleteConfirmDialog';
import TagSelector from '../manage/TagSelector';
import SchemaPreview from '../manage/SchemaPreview';
import { formatText } from '../../common/TextFormatterProvider';
import { getPeriodType, getPeriodFieldLabel, PERIOD_CONFIG, getDisplayColumnPrefix } from '../../../utils/periodUtils';
import { collection, onSnapshot, doc, query, where, serverTimestamp, deleteField, runTransaction } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { mergeSnapshotDocuments } from '../../../utils/liveSnapshots';
import { sortNewestFirst } from '../../../utils/filePages';
import useFileSectionPages from '../../../hooks/useFileSectionPages';
import { registerUploadBatch } from '../../../services/uploadRegistration';
import UploadRegistrationErrors from './UploadRegistrationErrors';
import DataProfileEditor from '../manage/DataProfileEditor';
import DeleteDataProfileDialog from '../manage/DeleteDataProfileDialog';
import ExistingFilesPrompt from '../../common/ExistingFilesPrompt';
import { findExistingFiles } from '../../../services/existingFiles';
import HelpButton from '../../common/HelpButton';
import LoadFailure, { loadFailureMessage } from '../../common/LoadFailure';
import { downloadFailureMessage } from '../../../services/api_utils';
import { deleteFile, gestureFailureMessage } from '../../../services/cleaningGestures';
import { downloadFiles } from '../../../services/fileDownloads';
import { getStoredValueLabel } from '../../../utils/functionParameterUtils';
import { bucketOf } from '../../../utils/ingestionStatus';
import { ACCEPTED_UPLOAD_EXTENSIONS, isAcceptedUpload, MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL, MAX_UPLOAD_FILES, MAX_UPLOAD_FILES_MESSAGE, MAX_UPLOAD_FILES_CHIP } from '../../../utils/uploadNames';
import { profileName, isDeletedProfile, sortedTagEntries } from '../../../utils/profileDisplay';
import { missingMandatoryAttributes, tagsToValueMap } from '../../../utils/mandatoryAttributes';

// ACCEPTED_UPLOAD_EXTENSIONS and isAcceptedUpload are shared via utils/uploadNames
// (one source of truth so the file-picker `accept` and the drag-drop guard agree).

// A file is listed in exactly one section, the bucket of its stored status
// (utils/ingestionStatus.bucketOf), read from the file document alone; each
// section is shown a page at a time. No card subscribes to unit documents.
//
// `blockedLandingBatchId` is the batch whose landing the table's claim holds
// `blocked`, or null (the Data page listens to the claim): a card of that batch
// says "Landing needs attention — contact support" in place of its status.


function ProfileDashboard({ profile, stats, companyId, tableId, table, blockedLandingBatchId = null, onFileUpload }) {
  const deleted = isDeletedProfile(profile);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [completedExpanded, setCompletedExpanded] = useState(false);
  const fileInputRef = useRef(null);
  const [stagedFiles, setStagedFiles] = useState([]);
  // Which selection the duplicate check in flight belongs to: a later pick
  // supersedes it, and its answer is dropped when it arrives.
  const checkTokenRef = useRef(0);
  const uploadOperationRef = useRef(null);
  useEffect(() => () => {
    checkTokenRef.current += 1;
    uploadOperationRef.current?.controller.abort();
    uploadOperationRef.current = null;
  }, [companyId, tableId, profile?.id]);
  // The last pick held more files than one upload may take.
  const [tooManyFiles, setTooManyFiles] = useState(false);
  const [conflictingFiles, setConflictingFiles] = useState([]);   // every staged file that already exists
  // One of this profile's listeners (its files, the profile doc) failed. What was
  // already loaded stays; a failed listener has stopped, so Retry resubscribes.
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // Targets of the pending bulk delete: { files, skipped } | null.
  const [bulkDeletePrompt, setBulkDeletePrompt] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  // One placeholder card per file of the upload in progress, keyed by the document id
  // the registration reserved for it, so the real document replaces it the moment the
  // listener delivers it. A placeholder whose upload did not register stays, marked
  // failed, until dismissed or the profile is left.
  const [temporaryFiles, setTemporaryFiles] = useState([]);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // The profile's file documents, live.
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkDownloadingSelected, setIsBulkDownloadingSelected] = useState(false);
  const [mappingValuesExpanded, setMappingValuesExpanded] = useState(false);
  const [profilePossibleValues, setProfilePossibleValues] = useState({});
  // The profile document's values as last synced, and the editor's current
  // store as of the last render — what the listener diffs against.
  const pvBaselineRef = useRef({});
  const pvLocalRef = useRef({});
  const [pvInputs, setPvInputs] = useState({});
  const [pvSaving, setPvSaving] = useState(false);
  const [pvError, setPvError] = useState(null);
  const [pvSuccess, setPvSuccess] = useState(null);
  const [deleteProfileDialogOpen, setDeleteProfileDialogOpen] = useState(false);
  const [recordSchemas, setRecordSchemas] = useState(null);
  const [schemasExpanded, setSchemasExpanded] = useState(false);
  const [schemaError, setSchemaError] = useState(null);
  // The learned record set the delete confirmation is about, by name: the backend
  // replaces the whole store on every document approval, so a position in the list
  // could point at a different set by the time the deletion is confirmed.
  const [deleteSetName, setDeleteSetName] = useState(null);
  const [isDeletingSet, setIsDeletingSet] = useState(false);
  const [extractionCorrections, setExtractionCorrections] = useState([]);
  const [correctionsExpanded, setCorrectionsExpanded] = useState(false);
  const [correctionsError, setCorrectionsError] = useState(null);
  const [deleteCorrectionId, setDeleteCorrectionId] = useState(null);
  const [isDeletingCorrection, setIsDeletingCorrection] = useState(false);
  // The file whose review dialog is open. The dialog is hosted here, outside every
  // section: a file changing status moves to another section and unmounts its card.
  const [reviewFileId, setReviewFileId] = useState(null);

  // Each section's files, newest upload first, placeholders included (a real document
  // with the same id wins the de-duplication). A placeholder is still uploading, so it
  // is processing; a file is in the bucket of its stored status.
  const sections = useMemo(() => {
    const bySection = { attention: [], deleting: [], processing: [], completed: [] };
    const unique = [...new Map([...temporaryFiles, ...files].map((file) => [file.id, file])).values()];
    unique.forEach((file) => {
      const section = file.isTemporary ? 'processing' : bucketOf(file.status);
      bySection[section].push(file);
    });
    return Object.fromEntries(Object.entries(bySection).map(([id, sectionFiles]) => [id, sortNewestFirst(sectionFiles)]));
  }, [files, temporaryFiles]);
  const { pages, next: nextPage, previous: previousPage } = useFileSectionPages(sections, profile?.id);
  // The files on screen: every section's page, Completed's only while it is open.
  const visibleFiles = useMemo(() => [
    ...pages.attention.files, ...pages.deleting.files, ...pages.processing.files,
    ...(completedExpanded ? pages.completed.files : []),
  ], [pages, completedExpanded]);

  useEffect(() => {
    // Reset state when profile changes (the section pages reset through their resetKey)
    setCompletedExpanded(false);
    setTemporaryFiles([]);
    setFiles([]);
    setIsLoading(true);
    setSelectedFileIds(new Set());
    setReviewFileId(null);
    setProfilePossibleValues({});
    pvBaselineRef.current = {};
    pvLocalRef.current = {};
    setPvInputs({});
    setPvError(null);
    setPvSuccess(null);
    setRecordSchemas(null);
    setSchemaError(null);
    setDeleteSetName(null);
    setExtractionCorrections([]);
    setCorrectionsError(null);
    setDeleteCorrectionId(null);
  }, [profile?.id]);

  // Keep selected tags in sync with live default_tags edits. Keyed on a
  // serialised value, not the object: the profiles listener rebuilds every
  // profile object on each snapshot, so an identity dep would re-fire this
  // (and, when it lived in the reset effect above, wedged isLoading) on any
  // profile-doc write — e.g. file_stats updates during an upload. Entries are
  // serialised sorted: Firestore map key order is not stable across snapshots,
  // and a pure order flip re-firing this would reset manually adjusted tags.
  const defaultTagsKey = JSON.stringify(sortedTagEntries(profile?.default_tags));
  useEffect(() => {
    if (profile?.default_tags) {
      const defaultTags = sortedTagEntries(profile.default_tags).map(([category, value]) => ({ category, value }));
      setSelectedTags(defaultTags);
    } else {
      setSelectedTags([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, defaultTagsKey]);

  // The profile's file documents, live. No unit document is subscribed for a card:
  // the file's ledger says what a card shows.
  useEffect(() => {
    if (!profile?.id || !companyId || !tableId) return;

    // The listener converts only the documents each snapshot changed and keeps every
    // other object as it was (utils/liveSnapshots): a large upload writes each ledger
    // many times, and rebuilding every file on each write stalled the page. What it has
    // built lives in this closure, so a new subscription (another profile, or Retry)
    // starts from nothing. Its first delivery is the initial load, so there is no
    // separate one-shot fetch: a getDocs cannot be cancelled, and one still in flight
    // when the user switched profile used to land the previous profile's files under
    // the new profile's heading. A listener delivers nothing after the cleanup below
    // unsubscribes it.
    setIsLoading(true);
    const filesRef = collection(db, `users/${companyId}/tables/${tableId}/files`);
    const filesQuery = query(filesRef, where('data_profile_id', '==', profile.id));
    setLoadError(false);
    let filesById = new Map();
    const unsubscribeFiles = onSnapshot(filesQuery, (snapshot) => {
      filesById = mergeSnapshotDocuments(filesById, snapshot);
      setFiles(Array.from(filesById.values()));
      setIsLoading(false);
    }, (error) => {
      console.error('Error fetching files:', error);
      setLoadError(true);
      setIsLoading(false);
    });

    return () => unsubscribeFiles();
  }, [profile?.id, companyId, tableId, loadAttempt]);

  // The profile doc is LIVE while the dashboard is open: approval-time learning
  // (possible values, record schemas, extraction corrections) lands in the
  // background, so the accordions below are current without a refresh.
  useEffect(() => {
    if (!companyId || !tableId || !profile?.id) return undefined;
    const profileRef = doc(db, `users/${companyId}/tables/${tableId}/data_profiles`, profile.id);
    return onSnapshot(profileRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data() || {};
        // The person's unsaved edits ride on top of the document as it now is.
        const remote = data.possible_values || {};
        const edits = diffValueEdits(pvBaselineRef.current, pvLocalRef.current);
        const next = applyValueEdits(remote, edits);
        pvBaselineRef.current = remote;
        pvLocalRef.current = next;
        setProfilePossibleValues(next);
        setRecordSchemas(data.record_schemas || null);
        setExtractionCorrections(data.extraction_corrections || []);
      } else {
        pvBaselineRef.current = {};
        pvLocalRef.current = {};
        setProfilePossibleValues({});
        setRecordSchemas(null);
        setExtractionCorrections([]);
      }
    }, (e) => {
      // The learned values already on screen are kept: a failed read is not an empty profile.
      console.error('Error watching profile doc:', e);
      setLoadError(true);
    });
  }, [companyId, tableId, profile?.id, loadAttempt]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFileUpload(Array.from(e.dataTransfer.files)); };
  // The input is cleared the moment its files are read, so the same file can be
  // chosen again after a cancelled duplicate prompt or a failed pre-check —
  // a file input only fires change when its value changes.
  const handleFileSelect = (e) => {
    const chosen = Array.from(e.target.files);
    e.target.value = '';
    handleFileUpload(chosen);
  };

  // Helpers for profile-level mapping values management
  const isValueLeaf = (node) => node && typeof node === 'object' && (
    Object.prototype.hasOwnProperty.call(node, 'updateBehavior') ||
    Object.prototype.hasOwnProperty.call(node, 'type')
  );

  // Only a time-series table has metrics; a leaf's mapping key is under its
  // period prefix ('period.Premium', 'period.Claims.Paid').
  const flattenValueLeaves = (schema, tableSchema, prefix = []) => {
    const leaves = [];
    Object.entries(schema || {}).forEach(([key, value]) => {
      if (isValueLeaf(value)) {
        const path = [...prefix, key];
        leaves.push({
          key: `${getDisplayColumnPrefix(tableSchema)}.${path.join('.')}`,
          label: path.join(' / ').replace(/_/g, ' ')
        });
      } else {
        const nested = (value && typeof value === 'object' && value.schema && typeof value.schema === 'object')
          ? value.schema
          : (value || {});
        leaves.push(...flattenValueLeaves(nested, tableSchema, [...prefix, key]));
      }
    });
    return leaves;
  };

  // Profile mapping values use a per-column text input, add/remove chips, and a
  // save that applies the editor's edits (see diffValueEdits) to the document
  // as it stands inside a transaction, so nothing learned or added elsewhere
  // since the editor synced is lost.
  // `localRef` mirrors the store the moment it changes — not on the next
  // render — so a document snapshot arriving between a click and its render
  // still diffs against the edit the click made.
  const makeValueEditorHandlers = ({ store, setStore, localRef, baselineRef, inputs, setInputs, setSaving, setError, setSuccess, label }) => ({
    handleInputChange: (columnKey, value) => {
      setInputs(prev => ({ ...prev, [columnKey]: value }));
    },
    handleAdd: (columnKey) => {
      const val = (inputs[columnKey] || '').trim();
      if (!val) return;
      const current = localRef.current;
      const next = { ...current, [columnKey]: [...(current[columnKey] || []), val] };
      localRef.current = next;
      setStore(next);
      setInputs(prev => ({ ...prev, [columnKey]: '' }));
    },
    handleRemove: (columnKey, index) => {
      const current = localRef.current;
      const next = { ...current, [columnKey]: (current[columnKey] || []).filter((_, i) => i !== index) };
      localRef.current = next;
      setStore(next);
    },
    save: async () => {
      if (!companyId || !tableId || !profile?.id) return;
      setSaving(true);
      setError(null);
      setSuccess(null);
      try {
        const edits = diffValueEdits(baselineRef.current, store);
        const merged = await persistProfileMappingValues(companyId, tableId, profile.id, edits);
        baselineRef.current = merged;
        localRef.current = merged;
        setStore(merged);
        setSuccess(`${label} saved.`);
        setTimeout(() => setSuccess(null), 2500);
      } catch (e) {
        console.error(`Error saving ${label.toLowerCase()}:`, e);
        setError(`Failed to save ${label.toLowerCase()}.`);
      } finally {
        setSaving(false);
      }
    },
  });

  const {
    handleInputChange: handlePvInputChange,
    handleAdd: handlePvAdd,
    handleRemove: handlePvRemove,
    save: saveProfileMappingValues,
  } = makeValueEditorHandlers({
    store: profilePossibleValues,
    setStore: setProfilePossibleValues,
    localRef: pvLocalRef,
    baselineRef: pvBaselineRef,
    inputs: pvInputs,
    setInputs: setPvInputs,
    setSaving: setPvSaving,
    setError: setPvError,
    setSuccess: setPvSuccess,
    label: 'Profile mapping values',
  });

  // record_schemas record sets -> SchemaPreview's {name, children} header nodes
  // (consecutive fields sharing a group render as one spanned parent band).
  const recordSetSchemaNodes = (recordSet) => {
    const nodes = [];
    (recordSet.fields || []).forEach((field) => {
      if (field.group) {
        const last = nodes[nodes.length - 1];
        if (last && last.name === field.group && last.children?.length) {
          last.children.push({ name: field.name, children: [] });
        } else {
          nodes.push({ name: field.group, children: [{ name: field.name, children: [] }] });
        }
      } else {
        nodes.push({ name: field.name, children: [] });
      }
    });
    return nodes;
  };

  // Forget one learned document layout. The store is removed from inside a transaction
  // that re-reads the profile, and the set is found by name in what the document holds
  // NOW — the backend replaces the whole store on every document approval, so a copy
  // taken when the bin was clicked may be missing a layout learned since, and a
  // position in that copy may no longer name the same set. The live document is what
  // the listener shows next, so no local state is patched here.
  const handleDeleteRecordSet = async () => {
    if (deleteSetName === null || !profile?.id) return;
    setIsDeletingSet(true);
    setSchemaError(null);
    try {
      const profileRef = doc(db, `users/${companyId}/tables/${tableId}/data_profiles`, profile.id);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(profileRef);
        if (!snap.exists()) throw new Error('This profile no longer exists.');
        const store = snap.data()?.record_schemas || {};
        const recordSets = store.record_sets || [];
        const matching = recordSets.filter((recordSet) => recordSet.name === deleteSetName);
        if (matching.length === 0) {
          throw new Error(`The layout "${deleteSetName}" is no longer in this profile's store.`);
        }
        if (matching.length > 1) {
          throw new Error(`More than one learned layout is named "${deleteSetName}" — reopen this list and try again.`);
        }
        const remaining = recordSets.filter((recordSet) => recordSet.name !== deleteSetName);
        if (remaining.length === 0) {
          // Last one gone: drop the whole store rather than leaving an empty
          // prior that would still be offered to schema discovery.
          tx.update(profileRef, { record_schemas: deleteField() });
          return;
        }
        // Replace the field wholesale (same semantics as the backend's
        // learning write). schema_hash is dropped: it hashed the full
        // discovered schema, which this store no longer is — the next
        // approved document upload rewrites the store with a fresh hash.
        tx.update(profileRef, {
          record_schemas: {
            record_sets: remaining,
            scalar_names: store.scalar_names || [],
            updated_at: serverTimestamp(),
          },
        });
      });
      setDeleteSetName(null);
    } catch (e) {
      console.error('Error deleting learned schema:', e);
      setSchemaError(e.message || 'Failed to delete the schema. Please try again.');
    } finally {
      setIsDeletingSet(false);
    }
  };

  // Un-learn one promoted extraction correction. Same shape as the learned-schema delete
  // above: the entry is removed, by id, from the list the document holds at the moment
  // of the write. An empty list is exactly as inert as an absent one here (nothing is
  // replayed from it), so the field is left in place rather than deleted when the last
  // entry goes.
  const handleDeleteCorrection = async () => {
    if (deleteCorrectionId === null || !profile?.id) return;
    setIsDeletingCorrection(true);
    setCorrectionsError(null);
    try {
      const profileRef = doc(db, `users/${companyId}/tables/${tableId}/data_profiles`, profile.id);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(profileRef);
        if (!snap.exists()) throw new Error('This profile no longer exists.');
        const corrections = snap.data()?.extraction_corrections || [];
        if (!corrections.some((entry) => entry.id === deleteCorrectionId)) {
          throw new Error('That correction is no longer in this profile\'s store.');
        }
        tx.update(profileRef, {
          extraction_corrections: corrections.filter((entry) => entry.id !== deleteCorrectionId),
        });
      });
      setDeleteCorrectionId(null);
    } catch (e) {
      console.error('Error deleting extraction correction:', e);
      setCorrectionsError(e.message || 'Failed to delete the correction. Please try again.');
    } finally {
      setIsDeletingCorrection(false);
    }
  };

  const handleFileUpload = async (uploadFiles) => {
    if (deleted) return;   // uploads disabled for deleted profiles (belt-and-braces)
    if (uploadFiles.length === 0) return;
    // One upload at a time: a second drop would run concurrently on the same
    // upload state, and the first to finish would report the page idle.
    if (isUploading || uploadOperationRef.current) {
      setUploadError('An upload is already in progress — add more files once it finishes.');
      return;
    }
    // The duplicate dialog is deciding about ONE selection: no other is taken
    // until it closes.
    if (confirmDialogOpen) return;
    setUploadError(null);
    setTooManyFiles(false);
    // The file-picker honours the `accept` filter, but drag-drop does not — mirror
    // it here so an unsupported file never reaches the backend (where it would only
    // segment to a Failed raw doc). Skip unsupported and oversized files; upload the rest.
    const wrongType = uploadFiles.filter((f) => !isAcceptedUpload(f.name));
    const tooLarge = uploadFiles.filter((f) => isAcceptedUpload(f.name) && f.size > MAX_UPLOAD_BYTES);
    const accepted = uploadFiles.filter((f) => isAcceptedUpload(f.name) && f.size <= MAX_UPLOAD_BYTES);
    const problems = [];
    if (wrongType.length) {
      problems.push(`Skipped unsupported file${wrongType.length > 1 ? 's' : ''}: `
        + `${wrongType.map((f) => f.name).join(', ')}. `
        + `Accepted types: ${ACCEPTED_UPLOAD_EXTENSIONS.join(', ')}.`);
    }
    if (tooLarge.length) {
      problems.push(`Max file size is ${MAX_UPLOAD_LABEL} — skipped: `
        + `${tooLarge.map((f) => f.name).join(', ')}.`);
    }
    if (problems.length) setUploadError(problems.join(' '));
    if (accepted.length === 0) return;
    if (accepted.length > MAX_UPLOAD_FILES) {
      setTooManyFiles(true);
      setUploadError(MAX_UPLOAD_FILES_MESSAGE);
      return;
    }
    const operation = {
      controller: new AbortController(), attributeMap: tagsToValueMap(selectedTags), started: false,
    };
    uploadOperationRef.current = operation;
    setIsUploading(true);
    setStagedFiles(accepted);
    // Check every file, not just up to the first hit, so the dialog can name all
    // the duplicates and offer to upload only the new ones. The answer is used
    // only if no later pick has superseded this one meanwhile.
    const token = checkTokenRef.current + 1;
    checkTokenRef.current = token;
    let conflicts;
    try {
      // This profile's raw files, and the table's unallocated queue (H28).
      conflicts = await findExistingFiles({ companyId, tableId, files: accepted, profileId: profile.id });
    } catch (error) {
      if (checkTokenRef.current !== token) return;
      console.error('Error checking for existing files:', error);
      setStagedFiles([]);
      setUploadError(`${loadFailureMessage('existing files')} Nothing was uploaded.`);
      uploadOperationRef.current = null;
      setIsUploading(false);
      return;
    }
    if (checkTokenRef.current !== token) return;
    if (conflicts.length > 0) {
      setConflictingFiles(conflicts);
      setConfirmDialogOpen(true);
      return;
    }
    await doUpload(accepted, operation);
  };

  const cancelPendingUpload = () => {
    const operation = uploadOperationRef.current;
    if (operation?.started) return;
    operation?.controller.abort();
    uploadOperationRef.current = null;
    checkTokenRef.current += 1;
    setConfirmDialogOpen(false);
    setStagedFiles([]);
    setConflictingFiles([]);
    setIsUploading(false);
  };

  const doUpload = async (uploadFiles, operation = uploadOperationRef.current) => {
    if (!operation || operation !== uploadOperationRef.current || operation.started) return;
    if (uploadFiles.length === 0) {
      cancelPendingUpload();
      return;
    }
    const missing = missingMandatoryAttributes(table?.attributeColumns || [], operation.attributeMap);
    if (missing.length > 0) {
      setUploadError(`Cannot upload: missing required attribute${missing.length > 1 ? 's' : ''}: `
        + `${missing.map(formatText).join(', ')}. Set the upload attributes, then retry.`);
      cancelPendingUpload();
      return;
    }
    operation.started = true;
    setConfirmDialogOpen(false);
    // A placeholder card per file the moment its document id is reserved, so a
    // multi-file upload of large workbooks is visible while the bytes move. Each is
    // replaced by its real document when the listener delivers it, and one whose upload
    // did not register is marked failed under the same id.
    const showPlaceholders = (registered) => {
      if (uploadOperationRef.current !== operation) return;
      setTemporaryFiles(registered.map(({ id, file }) => ({
        id,
        name: file.name,
        created_at: new Date(),
        data_profile_id: profile.id,
        isTemporary: true,
      })));
    };
    // `failures` maps a placeholder id to why its upload did not register.
    const markPlaceholdersFailed = (failures) => {
      setTemporaryFiles((prev) => prev.map((placeholder) => (
        failures.has(placeholder.id)
          ? { ...placeholder, uploadFailed: true, uploadError: failures.get(placeholder.id) }
          : placeholder
      )));
    };
    try {
      const { failed } = await registerUploadBatch({
        companyId, tableId, profileId: profile.id, files: uploadFiles,
        attributeMap: operation.attributeMap, signal: operation.controller.signal,
        onRegistered: showPlaceholders,
      });
      if (uploadOperationRef.current !== operation || operation.controller.signal.aborted) return;
      if (failed.length > 0) {
        markPlaceholdersFailed(new Map(failed.map(({ id, error }) => [id, error?.message || null])));
        setUploadError(`${failed.length} file(s) did not upload — add them again: `
          + `${failed.map(({ file }) => file.name).join(', ')}.`);
      }
      if (onFileUpload) onFileUpload();
    } catch (error) {
      if (uploadOperationRef.current === operation && !operation.controller.signal.aborted) {
        console.error('Upload error:', error);
        setUploadError(error.message || 'Failed to upload files.');
        // Nothing says which members registered — some may well have, and their
        // documents will replace their placeholders as they arrive. The rest are
        // marked unconfirmed, never failed: telling the user to add them again
        // could duplicate a file that did land.
        setTemporaryFiles((prev) => prev.map((placeholder) => (
          { ...placeholder, uploadUnconfirmed: true, uploadError: error.message || null }
        )));
      }
    } finally {
      if (uploadOperationRef.current === operation) {
        uploadOperationRef.current = null;
        setIsUploading(false);
        setStagedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  // A placeholder is done the moment its real document arrives.
  useEffect(() => {
    const arrived = new Set(files.map((file) => file.id));
    setTemporaryFiles((prev) => (
      prev.some((placeholder) => arrived.has(placeholder.id))
        ? prev.filter((placeholder) => !arrived.has(placeholder.id))
        : prev
    ));
  }, [files]);

  // The file cards redraw only when one of their inputs changes (React.memo), so
  // what every card is handed — these handlers and the profile name — keeps its
  // identity from render to render.
  const handleDismissPlaceholder = useCallback((placeholderId) => {
    setTemporaryFiles((prev) => prev.filter((placeholder) => placeholder.id !== placeholderId));
  }, []);

  const handleFileSelectChange = useCallback((checked, fileId) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(fileId); else next.delete(fileId);
      return next;
    });
  }, []);

  // Selections are kept only for what is on screen: a file leaving view is
  // unticked, so bulk actions only ever act on visible pages.
  useEffect(() => {
    const visibleIds = new Set(visibleFiles.map((file) => file.id));
    setSelectedFileIds((prev) => {
      const next = new Set([...prev].filter((id) => visibleIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleFiles]);

  const shownProfileName = profileName(profile);
  const tableName = formatText(table?.name || tableId);
  const profileNameById = useMemo(
    () => ({ [profile?.id]: shownProfileName }),
    [profile?.id, shownProfileName],
  );

  const handleUploadAnyway = () => doUpload(stagedFiles);
  const handleUploadSkippingDuplicates = () => {
    const skip = new Set(conflictingFiles);
    return doUpload(stagedFiles.filter(file => !skip.has(file)));
  };

  // The file list is drawn once the files have been delivered; a listener that
  // failed shows its retry instead.
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading files for {profileName(profile)}...</Typography>
      </Box>
    );
  }

  // One section's page of file cards, with a pager when the section has more than one
  // page. The title counts every file in the section.
  const renderFileGroup = (sectionId, groupTitle) => {
    const page = pages[sectionId];
    if (sections[sectionId].length === 0) return null;
    return (
      <Box sx={{ mb: 3 }}>
        {groupTitle && <Typography variant="h6" gutterBottom>{groupTitle} ({sections[sectionId].length})</Typography>}
        <Grid container spacing={2}>
          {page.files.map(file => (
            <Grid item xs={12} md={6} xl={4} key={file.id}>
              <FileCard
                file={file}
                companyId={companyId}
                tableId={tableId}
                tableName={tableName}
                profileNameById={profileNameById}
                selected={selectedFileIds.has(file.id)}
                landingBlocked={blockedLandingBatchId !== null && file.batch_id === blockedLandingBatchId}
                onSelectChange={handleFileSelectChange}
                onOpen={setReviewFileId}
                onDismiss={handleDismissPlaceholder}
              />
            </Grid>
          ))}
        </Grid>
        {(page.hasPrevious || page.hasNext) && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button size="small" disabled={!page.hasPrevious} onClick={() => previousPage(sectionId)}>
              Previous page
            </Button>
            <Typography variant="body2">Page {page.pageNumber}</Typography>
            <Button size="small" disabled={!page.hasNext} onClick={() => nextPage(sectionId)}>
              Next page
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // A zip of files: each file, its originals and its tables' cleaned workbooks.
  // Each file's units are read when the button is pressed (services/fileDownloads).
  const downloadAsZip = async (chosenFiles, zipName) => {
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const { savedCount, failed } = await downloadFiles(companyId, tableId, sortNewestFirst(chosenFiles), zipName);
      if (failed.length > 0) setUploadError(downloadFailureMessage(failed, savedCount));
    } catch (error) {
      console.error('Bulk download error:', error);
      setUploadError('The download could not be prepared. Try again.');
    }
  };

  const handleDownloadAll = async () => {
    if (files.length === 0) {
      setUploadError('This profile has no files to download.');
      return;
    }
    setIsBulkDownloading(true);
    await downloadAsZip(files, `${tableId}_${profileName(profile)}.zip`);
    setIsBulkDownloading(false);
  };

  const handleBulkDownloadSelected = async () => {
    setIsBulkDownloadingSelected(true);
    await downloadAsZip(files.filter((file) => selectedFileIds.has(file.id)), `${tableId}_${profileName(profile)}_selected.zip`);
    setIsBulkDownloadingSelected(false);
  };

  // Delete is a two-step: the button collects the eligible files and names them in
  // a confirmation; only Confirm requests the deletions, which the table's queue
  // then carries out irreversibly.
  const requestBulkDelete = async () => {
    try {
      setBulkDeletePrompt(await collectBulkDeleteTargets({ companyId, tableId, files, selectedFileIds }));
    } catch (error) {
      setUploadError(gestureFailureMessage(error));
    }
  };

  // Each file's deletion is requested in its own transaction, one after another: a
  // write batch cannot read, and every request is judged on its live file (Spec 5
  // section 5). A refusal is reported and the rest continue.
  const handleBulkDeleteConfirmed = async () => {
    if (!bulkDeletePrompt) return;
    setIsBulkDeleting(true);
    const refusals = [];
    for (const target of bulkDeletePrompt.files) {
      try {
        await deleteFile(companyId, tableId, target.id);
      } catch (error) {
        refusals.push(`${target.label} (${gestureFailureMessage(error)})`);
      }
    }
    setSelectedFileIds(new Set());
    setBulkDeletePrompt(null);
    setIsBulkDeleting(false);
    if (refusals.length > 0) setUploadError(`Could not delete ${refusals.join('; ')}.`);
  };

  const clearSelections = () => {
    setSelectedFileIds(new Set());
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>{profileName(profile)}</Typography>
          {deleted && <Chip label="Deleted" color="default" size="small" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <HelpButton
            docPath="data-page/uploading-data/reviewing-uploads/how-cleaning-works"
            tooltip="Data Profile Documentation"
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadAll}
            disabled={isBulkDownloading}
          >
            {isBulkDownloading ? <CircularProgress size={16} /> : 'Download All'}
          </Button>
          {!deleted && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setIsEditingProfile(true)}
            >
              Edit Profile
            </Button>
          )}
          {!deleted && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setDeleteProfileDialogOpen(true)}
            >
              Delete Profile
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mr: 1, mb: 1 }}>
          Data Profile Attributes:
        </Typography>
        {profile?.default_tags && sortedTagEntries(profile.default_tags).map(([category, value]) => (
          <Chip key={category} label={`${formatText(category)}: ${getStoredValueLabel(value)}`} size="small" sx={{ mr: 1, mb: 1 }} />
        ))}
      </Box>
      {table && (
        <Accordion expanded={mappingValuesExpanded} onChange={() => setMappingValuesExpanded(!mappingValuesExpanded)} sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={mappingValuesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
            <Typography variant="h6">Profile Mapping Values</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {pvError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPvError(null)}>{pvError}</Alert>}
            {pvSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPvSuccess(null)}>{pvSuccess}</Alert>}

            {/* Identifier */}
            {table.identifier?.name && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                  Identifier
                </Typography>
                {(() => {
                  const columnName = table.identifier.name;
                  const values = profilePossibleValues[columnName] || [];
                  return (
                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {columnName.replace(/_/g, ' ')}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
                        {values.map((value, index) => (
                          <Chip
                            key={`${columnName}-${index}`}
                            label={value || '(empty)'}
                            onDelete={deleted ? undefined : () => handlePvRemove(columnName, index)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                      </Stack>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          placeholder="Add mapping value"
                          disabled={deleted}
                          value={pvInputs[columnName] || ''}
                          onChange={(e) => handlePvInputChange(columnName, e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handlePvAdd(columnName)}
                          sx={{ flexGrow: 1, maxWidth: 400 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handlePvAdd(columnName)}
                          color="primary"
                          disabled={deleted || !((pvInputs[columnName] || '').trim())}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                    </Paper>
                  );
                })()}
              </Box>
            )}

            {/* Static Columns */}
            {table.detailColumns?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  {table?.tableType === 'raw' ? 'Columns' : 'Details Columns'}
                </Typography>
                <Grid container spacing={2}>
                  {table.detailColumns.map((column) => {
                    const columnName = column.name;
                    const values = profilePossibleValues[columnName] || [];
                    return (
                      <Grid item xs={12} md={6} key={columnName}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Typography variant="subtitle2" gutterBottom>{columnName.replace(/_/g, ' ')}</Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
                            {values.map((value, index) => (
                              <Chip
                                key={`${columnName}-${index}`}
                                label={value || '(empty)'}
                                onDelete={deleted ? undefined : () => handlePvRemove(columnName, index)}
                                size="small"
                                sx={{ mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              placeholder="Add value"
                              disabled={deleted}
                              value={pvInputs[columnName] || ''}
                              onChange={(e) => handlePvInputChange(columnName, e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handlePvAdd(columnName)}
                              sx={{ flexGrow: 1 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handlePvAdd(columnName)}
                              color="primary"
                              disabled={deleted || !((pvInputs[columnName] || '').trim())}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* Period Field */}
            {table.tableType === 'timeSeries' && (() => {
              const periodType = getPeriodType(table);
              const periodFieldLabel = getPeriodFieldLabel(periodType);
              const periodConfig = PERIOD_CONFIG[periodType];
              const columnName = getDisplayColumnPrefix(table);
              const values = profilePossibleValues[columnName] || [];
              return (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>{periodFieldLabel}</Typography>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom>{periodConfig?.label || 'Period'}</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
                      {values.map((value, index) => (
                        <Chip
                          key={`${columnName}-${index}`}
                          label={value || '(empty)'}
                          onDelete={deleted ? undefined : () => handlePvRemove(columnName, index)}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder={`Add ${periodConfig?.label?.toLowerCase() || 'period'} mapping value`}
                        disabled={deleted}
                        value={pvInputs[columnName] || ''}
                        onChange={(e) => handlePvInputChange(columnName, e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handlePvAdd(columnName)}
                        sx={{ flexGrow: 1 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handlePvAdd(columnName)}
                        color="primary"
                        disabled={deleted || !((pvInputs[columnName] || '').trim())}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                </Box>
              );
            })()}

            {/* Metrics Columns */}
            {table.metricColumns?.schema && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Metrics Columns</Typography>
                <Grid container spacing={2}>
                  {flattenValueLeaves(table.metricColumns.schema, table).map((item) => {
                    const columnName = item.key;
                    const values = profilePossibleValues[columnName] || [];
                    return (
                      <Grid item xs={12} md={6} key={columnName}>
                        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                          <Typography variant="subtitle2" gutterBottom>{item.label}</Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1, minHeight: 32 }}>
                            {values.map((value, index) => (
                              <Chip
                                key={`${columnName}-${index}`}
                                label={value || '(empty)'}
                                onDelete={deleted ? undefined : () => handlePvRemove(columnName, index)}
                                size="small"
                                sx={{ mb: 0.5 }}
                              />
                            ))}
                          </Stack>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              placeholder="Add value or formula"
                              disabled={deleted}
                              value={pvInputs[columnName] || ''}
                              onChange={(e) => handlePvInputChange(columnName, e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handlePvAdd(columnName)}
                              sx={{ flexGrow: 1 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handlePvAdd(columnName)}
                              color="primary"
                              disabled={deleted || !((pvInputs[columnName] || '').trim())}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={saveProfileMappingValues}
                disabled={deleted || pvSaving}
              >
                {pvSaving ? 'Saving...' : 'Save Mapping Values'}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion expanded={schemasExpanded} onChange={() => setSchemasExpanded(!schemasExpanded)} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={schemasExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
          <Typography variant="h6">Learned Document Schemas</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {schemaError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSchemaError(null)}>{schemaError}</Alert>}
          {(recordSchemas?.record_sets?.length || 0) === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No document layouts learned yet. When you approve a PDF or Word upload, Decode
              remembers its record layout here and reuses it on future uploads.
            </Typography>
          ) : (
            <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Record layouts learned from this profile's document uploads. They guide schema
              discovery on the next upload; deleting one means that layout is rediscovered
              from scratch and re-learned at the next approval.
            </Typography>
            {recordSchemas.record_sets.map((recordSet, index) => (
              <Paper key={`${recordSet.name}-${index}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{recordSet.name}</Typography>
                    {recordSet.grain && (
                      <Typography variant="body2" color="text.secondary">{recordSet.grain}</Typography>
                    )}
                  </Box>
                  <Tooltip title="Delete this schema">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleted}
                        onClick={() => setDeleteSetName(recordSet.name)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <SchemaPreview schema={recordSetSchemaNodes(recordSet)} />
                {recordSet.notes_for_extractor && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Notes for extractor</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {recordSet.notes_for_extractor}
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={correctionsExpanded} onChange={() => setCorrectionsExpanded(!correctionsExpanded)} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={correctionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
          <Typography variant="h6">Extraction Corrections</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {correctionsError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCorrectionsError(null)}>{correctionsError}</Alert>}
          {extractionCorrections.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No corrections learned yet. While reviewing a document upload, tick "Remember for
              future uploads" on a fix (or pin its chip) and approve — it will appear here and be
              applied to every future upload.
            </Typography>
          ) : (
            <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Fixes you asked for while reviewing this profile's document uploads, kept once you
              approved the result. Every later upload is extracted with them applied; deleting one
              stops it being applied, and it is re-learned the next time you approve a unit
              corrected that way.
            </Typography>
            {extractionCorrections.map((correction) => (
              <Paper
                key={correction.id}
                variant="outlined"
                sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
              >
                <Typography variant="body2">{correction.text}</Typography>
                <Tooltip title="Delete this correction">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={deleted}
                      onClick={() => setDeleteCorrectionId(correction.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Paper>
            ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h4">{stats.totalFiles || 0}</Typography><Typography variant="body2" color="text.secondary">Total Files</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h4" color="success.main">{stats.completed || 0}</Typography><Typography variant="body2" color="text.secondary">Completed</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h4" color="warning.main">{stats.needsAttention || 0}</Typography><Typography variant="body2" color="text.secondary">Need Review</Typography></Paper></Grid>
        <Grid item xs={12} sm={6} md={3}><Paper sx={{ p: 2, textAlign: 'center' }}><Typography variant="h4" color="info.main">{stats.processing || 0}</Typography><Typography variant="body2" color="text.secondary">Processing</Typography></Paper></Grid>
      </Grid>
      {deleted ? (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Alert severity="info">
            This profile is deleted — uploads are disabled. Files below remain viewable and manageable.
          </Alert>
        </Paper>
      ) : (
        <Paper sx={{ mb: 3, border: '2px dashed', borderColor: tooManyFiles ? 'error.main' : (isDragging ? 'primary.main' : 'divider'), backgroundColor: isDragging ? 'action.hover' : 'transparent' }}>
          <Box sx={{ p: 3, textAlign: 'center', cursor: 'pointer' }} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6">Drop files here or click to upload</Typography>
            {tooManyFiles && <Chip label={MAX_UPLOAD_FILES_CHIP} color="error" size="small" sx={{ mt: 1 }} />}
            <input ref={fileInputRef} type="file" multiple accept={ACCEPTED_UPLOAD_EXTENSIONS.join(',')} style={{ display: 'none' }} onChange={handleFileSelect} />
            {isUploading && <Box sx={{ mt: 2 }}><CircularProgress size={24} /><Typography variant="body2" sx={{ mt: 1 }}>Uploading...</Typography></Box>}
          </Box>
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.default' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Current Upload Attributes:
            </Typography>
            <TagSelector companyId={companyId} tableMetadataColumns={table?.attributeColumns || []} selectedTags={selectedTags} onTagsChange={setSelectedTags} />
          </Box>
        </Paper>
      )}
      <UploadRegistrationErrors companyId={companyId} tableId={tableId} profileId={profile.id} />
      {uploadError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>{uploadError}</Alert>}
      {loadError && (
        <LoadFailure thing="this profile's files" onRetry={() => setLoadAttempt((n) => n + 1)} align="left" sx={{ p: 0, mb: 2 }} />
      )}
      {uploadSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(null)}>{uploadSuccess}</Alert>}
      {(() => {
        const selectedCount = selectedFileIds.size;
        if (selectedCount === 0) return null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2">{selectedCount} {selectedCount === 1 ? 'file' : 'files'} selected</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleBulkDownloadSelected}
                disabled={isBulkDownloadingSelected}
              >
                {isBulkDownloadingSelected ? <CircularProgress size={16} /> : 'Download'}
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                size="small"
                onClick={requestBulkDelete}
                disabled={isBulkDeleting}
              >
                Delete
              </Button>
              <Button variant="text" size="small" onClick={clearSelections}>Clear</Button>
            </Box>
          </Box>
        );
      })()}
      
      {renderFileGroup('attention', 'Needs Attention')}
      {renderFileGroup('deleting', 'Deleting')}
      {renderFileGroup('processing', 'Processing')}
      {sections.completed.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Completed ({sections.completed.length})</Typography>
            <IconButton
              aria-label={completedExpanded ? 'Hide completed files' : 'Show completed files'}
              onClick={() => setCompletedExpanded(!completedExpanded)}
            >
              {completedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          {/* Closed, it holds no cards. */}
          <Collapse in={completedExpanded} unmountOnExit>
            {renderFileGroup('completed', '')}
          </Collapse>
        </Box>
      )}
      
      {/* The review dialog — mounted here, outside every file group, so that a file changing
          status (an edit reruns it, moving it from one group to another) tears down its
          card without taking the open dialog with it. */}
      {reviewFileId && table && (
        <FileReviewDialog
          key={reviewFileId}
          open
          companyId={companyId}
          tableId={tableId}
          table={table}
          fileId={reviewFileId}
          onClose={() => setReviewFileId(null)}
          onNotice={setUploadError}
        />
      )}

      <BulkDeleteConfirmDialog
        open={Boolean(bulkDeletePrompt)}
        targets={bulkDeletePrompt}
        tableName={tableName}
        busy={isBulkDeleting}
        onCancel={() => setBulkDeletePrompt(null)}
        onConfirm={handleBulkDeleteConfirmed}
      />

      <ExistingFilesPrompt
        open={confirmDialogOpen}
        conflicts={conflictingFiles.map((file) => ({ file }))}
        total={stagedFiles.length}
        scope="for this profile"
        onCancel={cancelPendingUpload}
        onSkip={handleUploadSkippingDuplicates}
        onUploadAnyway={handleUploadAnyway}
      />

      <DeleteDataProfileDialog
        open={deleteProfileDialogOpen}
        profile={profile}
        companyId={companyId}
        tableId={tableId}
        onClose={() => setDeleteProfileDialogOpen(false)}
      />

      <Dialog
        open={deleteSetName !== null}
        onClose={() => !isDeletingSet && setDeleteSetName(null)}
      >
        <DialogTitle>Delete Learned Schema</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the learned schema "{deleteSetName || ''}"?
            The next document upload rediscovers this layout from scratch, and the next
            approval re-learns it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSetName(null)} disabled={isDeletingSet}>
            Cancel
          </Button>
          <Button onClick={handleDeleteRecordSet} color="error" disabled={isDeletingSet}>
            {isDeletingSet ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteCorrectionId !== null}
        onClose={() => !isDeletingCorrection && setDeleteCorrectionId(null)}
      >
        <DialogTitle>Delete Extraction Correction</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the correction
            "{extractionCorrections.find((entry) => entry.id === deleteCorrectionId)?.text || ''}"?
            New document uploads stop applying it. Data already ingested is unaffected, and the
            next approval of a unit corrected that way re-learns it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCorrectionId(null)} disabled={isDeletingCorrection}>
            Cancel
          </Button>
          <Button onClick={handleDeleteCorrection} color="error" disabled={isDeletingCorrection}>
            {isDeletingCorrection ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {isEditingProfile && (
        <DataProfileEditor
          open={true}
          onClose={(savedProfile) => {
            if (savedProfile && savedProfile.default_tags) {
              const tagArray = sortedTagEntries(savedProfile.default_tags).map(([category, value]) => ({ category, value }));
              setSelectedTags(tagArray);
            }
            setIsEditingProfile(false);
          }}
          table={table}
          initialProfile={profile}
          companyId={companyId}
          tableId={tableId}
        />
      )}
    </Box>
  );
}

export default ProfileDashboard;
